<template>
    <!-- Workspace do agente: conversa à esquerda, painel de visões à direita
         (empilha em telas estreitas). O agente opera o painel: abre visões,
         troca o período, gera parecer e dossiê. -->
    <div
        ref="workspace"
        class="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(20,21,26,0.04)] lg:flex-row dark:border-white/10 dark:bg-gray-900"
        :style="workspaceStyle"
    >
        <!-- Agente -->
        <div
            ref="agent"
            class="flex min-h-[560px] shrink-0 flex-col border-b border-gray-200 transition-shadow duration-500 lg:min-h-0 lg:w-[400px] lg:border-b-0 lg:border-r xl:w-[440px] dark:border-white/10"
            :class="flashClass('agent', true)"
        >
            <seller-ai-audit
                ref="agentPane"
                class="min-h-0 flex-1"
                :user="user"
                :views="viewTabs"
                :active-view="view"
                :period="period"
                :period-options="periodOptions"
                :cards="portrait?.cards || []"
                :signals="farolSignals"
                :signals-loading="portraitLoading"
                :has-open-case="Boolean(openCheckpoint)"
                @navigate="goNav"
                @tour="startTour"
                @period="period = $event"
                @dossier="generateDossier"
                @decide="focusDecision"
            />
        </div>

        <!-- Painel de visões -->
        <div
            class="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50/70 dark:bg-gray-950/40"
        >
            <!-- Abas + período + dossiê -->
            <div
                class="flex h-12 shrink-0 items-center gap-3 border-b border-gray-200 bg-white pl-2 pr-3 dark:border-white/10 dark:bg-gray-900"
            >
                <nav
                    class="no-scrollbar flex min-w-0 flex-1 items-stretch self-stretch overflow-x-auto"
                    aria-label="Visões do seller"
                    role="tablist"
                >
                    <button
                        v-for="tab in viewTabs"
                        :key="tab.key"
                        type="button"
                        role="tab"
                        :aria-selected="view === tab.key"
                        class="relative flex shrink-0 items-center gap-1.5 px-3 text-[13px] transition-colors focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-white/5"
                        :class="
                            view === tab.key
                                ? 'font-medium text-gray-900 dark:text-white'
                                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                        "
                        :title="tab.summary || tab.label"
                        @click="openView(tab.key)"
                    >
                        {{ tab.label }}
                        <span
                            v-if="priorityDot(tab.priority)"
                            class="h-1.5 w-1.5 rounded-full"
                            :class="priorityDot(tab.priority)"
                        ></span>
                        <span
                            v-if="view === tab.key"
                            class="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-gray-900 dark:bg-white"
                        ></span>
                    </button>
                </nav>

                <button
                    type="button"
                    :disabled="generatingDossier"
                    v-tooltip="'Gerar dossiê do usuário'"
                    class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-transparent dark:text-gray-200 dark:hover:bg-white/5"
                    @click="generateDossier"
                >
                    <LoaderCircleIcon
                        v-if="generatingDossier"
                        class="size-3.5 animate-spin"
                        aria-hidden="true"
                    />
                    <FileTextIcon v-else class="size-3.5" aria-hidden="true" />
                    {{ generatingDossier ? "Gerando..." : "Dossiê" }}
                </button>
            </div>

            <!-- Conteúdo da visão -->
            <div ref="canvas" class="relative min-h-0 flex-1 overflow-y-auto">
                <div
                    v-for="tab in viewTabs"
                    :key="tab.key"
                    role="tabpanel"
                    :aria-hidden="view !== tab.key"
                    :class="
                        view === tab.key
                            ? 'flex flex-col gap-4 p-5'
                            : 'pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-hidden p-5'
                    "
                >
                    <!-- Cabeçalho da visão: título, leitura e atalho para o
                         agente -->
                    <div
                        class="flex flex-wrap items-start justify-between gap-3"
                    >
                        <div class="flex min-w-0 flex-col gap-0.5">
                            <h2
                                class="text-base font-semibold tracking-tight text-gray-900 dark:text-white"
                            >
                                {{ tab.label }}
                            </h2>
                            <p
                                class="text-[13px] text-gray-500 dark:text-gray-400"
                            >
                                {{ tab.summary || tab.description }}
                            </p>
                        </div>
                        <div class="flex shrink-0 items-center gap-2">
                            <div
                                role="group"
                                aria-label="Selecionar período de análise"
                                v-tooltip="
                                    'Janela de análise: ' + periodRangeLabel
                                "
                                class="inline-flex shrink-0 items-center gap-0.5 rounded-lg bg-gray-200/60 p-0.5 dark:bg-white/5"
                            >
                                <button
                                    v-for="option in periodOptions"
                                    :key="option"
                                    type="button"
                                    :aria-pressed="period === option"
                                    @click="period = option"
                                    :class="[
                                        'rounded-md px-2 py-1 text-xs tabular-nums transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                                        period === option
                                            ? 'bg-white font-medium text-gray-900 shadow-xs dark:bg-white/15 dark:text-white'
                                            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                                    ]"
                                >
                                    {{ option === 0 ? "Total" : option + "d" }}
                                </button>
                            </div>
                            <button
                                type="button"
                                class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:text-white"
                                @click="askAgent(tab.question)"
                            >
                                <SparklesIcon
                                    class="size-3.5 text-indigo-500"
                                    aria-hidden="true"
                                />
                                {{ tab.question }}
                            </button>
                        </div>
                    </div>

                    <!-- Visão geral -->
                    <template v-if="tab.key === 'overview'">
                        <risk-case-banner
                            v-if="openCheckpoint"
                            :headline="caseHeadline"
                            :meta="caseMeta"
                        />

                        <div
                            class="rounded-xl transition-shadow duration-500"
                            :class="flashClass('julg')"
                        >
                            <seller-farol
                                :signals="farolSignals"
                                :loading="portraitLoading"
                                @go="goNav"
                            />
                        </div>

                        <seller-portrait-grid
                            :portrait="portrait"
                            :loading="portraitLoading"
                            :error="portraitError"
                            :keys="[
                                'coverage',
                                'exposure',
                                'reserve',
                                'cashouts',
                                'card_share',
                            ]"
                            @open-detail="goNav('retrato')"
                        />
                    </template>

                    <!-- Reserva & Runoff -->
                    <div
                        v-else-if="tab.key === 'runoff'"
                        class="rounded-xl transition-shadow duration-500"
                        :class="flashClass('runoff')"
                    >
                        <reserve-runoff-card
                            :key="runoffKey"
                            :user="user"
                            @data-loaded="updateRunoffSummary"
                        />
                    </div>

                    <!-- Conduta & Score -->
                    <template v-else-if="tab.key === 'conduta'">
                        <seller-portrait-grid
                            :portrait="portrait"
                            :loading="portraitLoading"
                            :error="portraitError"
                            :keys="['disputes', 'box_disputes', 'refunds']"
                            @open-detail="goNav('retrato')"
                        />

                        <div
                            ref="score"
                            class="rounded-lg transition-shadow duration-500"
                            :class="flashClass('score')"
                        >
                            <score
                                :user="user"
                                @data-loaded="updateScoreSummary"
                            />
                        </div>

                        <div
                            class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                            <collapsible-info-card
                                title="Análise do Sistema"
                                icon="ExclamationCircleIcon"
                                :collapsed="!userAnalysis"
                                :priority="systemAnalysisPriority"
                                :summary="systemAnalysisSummary"
                                :badge="systemAnalysisBadge"
                                :loading="!userAnalysis"
                            >
                                <user-system-analysis
                                    :user="user"
                                    @data-loaded="updateSystemAnalysisSummary"
                                />
                            </collapsible-info-card>

                            <collapsible-info-card
                                title="BINs de Cartão Mais Usados"
                                icon="CreditCardIcon"
                                :collapsed="true"
                                :summary="binSummary"
                                :priority="binPriority"
                                :badge="binBadge"
                                :period-label="periodLabel"
                                :loading="binSummary === ''"
                            >
                                <user-bin-usage-card
                                    :user="user"
                                    :period="period"
                                    @data-loaded="updateBinSummary"
                                />
                            </collapsible-info-card>
                        </div>
                    </template>

                    <!-- Operação & Vendas -->
                    <template v-else-if="tab.key === 'operacao'">
                        <seller-portrait-grid
                            :portrait="portrait"
                            :loading="portraitLoading"
                            :error="portraitError"
                            :keys="['tpv', 'sales_count', 'days_with_sale']"
                            @open-detail="goNav('retrato')"
                        />

                        <commission-cards
                            :user="user"
                            :period="period"
                        ></commission-cards>

                        <commissions-chart
                            :user="user"
                            :filters="chartFilters"
                            :period="period"
                        />

                        <div
                            class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                        >
                            <collapsible-info-card
                                title="Métodos de Pagamento"
                                icon="BanknotesIcon"
                                :collapsed="true"
                                :summary="paymentMethodSummary"
                                :priority="paymentMethodPriority"
                                :badge="paymentMethodBadge"
                                :period-label="periodLabel"
                                :loading="paymentMethodSummary === ''"
                            >
                                <user-payment-method-card
                                    :user="user"
                                    :period="period"
                                    @data-loaded="updatePaymentMethodSummary"
                                />
                            </collapsible-info-card>

                            <collapsible-info-card
                                title="Tipos de Comissão"
                                icon="ChartBarIcon"
                                :collapsed="true"
                                :summary="commissionTypesSummary"
                                :priority="commissionTypesPriority"
                                :badge="commissionTypesBadge"
                                :loading="commissionTypesSummary === ''"
                            >
                                <user-commission-types-card
                                    :user="user"
                                    @data-loaded="updateCommissionTypesSummary"
                                />
                            </collapsible-info-card>
                        </div>
                    </template>

                    <!-- Histórico de decisões -->
                    <template v-else-if="tab.key === 'hist'">
                        <div
                            v-if="decidedCheckpoints.length === 0"
                            class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center dark:border-white/10 dark:bg-transparent"
                        >
                            <HistoryIcon
                                class="size-5 text-gray-300 dark:text-gray-600"
                                aria-hidden="true"
                            />
                            <p class="text-sm text-gray-500 dark:text-gray-400">
                                Nenhuma decisão de reserva registrada para este
                                seller.
                            </p>
                        </div>
                        <ol
                            v-else
                            class="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900"
                        >
                            <li
                                v-for="checkpoint in decidedCheckpoints"
                                :key="checkpoint.id"
                                class="flex gap-3 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-white/5"
                            >
                                <span
                                    class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                                    :class="
                                        checkpoint.status === 'decided'
                                            ? 'bg-indigo-500'
                                            : 'bg-gray-300 dark:bg-gray-600'
                                    "
                                ></span>
                                <div class="flex flex-col gap-0.5">
                                    <span
                                        class="text-[13px] font-medium text-gray-900 dark:text-white"
                                    >
                                        {{ historyTitle(checkpoint) }}
                                    </span>
                                    <span
                                        class="text-xs text-gray-400 dark:text-gray-500"
                                    >
                                        {{ historyMeta(checkpoint) }}
                                    </span>
                                    <span
                                        v-if="checkpoint.decision_note"
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        “{{ checkpoint.decision_note }}”
                                    </span>
                                </div>
                            </li>
                        </ol>
                    </template>

                    <!-- Retrato completo: carrega na primeira visita -->
                    <template v-else-if="tab.key === 'retrato'">
                        <seller-snapshot-panel
                            v-if="visited.retrato"
                            :user="user"
                        />
                    </template>
                </div>
            </div>

            <!-- Decisão do caso: rodapé do painel enquanto o caso está
                 aberto -->
            <risk-decision-bar
                class="shrink-0"
                :inline="true"
                :checkpoint="openCheckpoint"
                :extend-options="extendOptions"
                :flash="flash === 'bar'"
                @decided="onDecided"
                @dismiss="decidedRecently = false"
            />
        </div>

        <metrics-tour ref="tour" @highlight="onTourHighlight" />
    </div>
</template>

<script>
import CommissionCards from "../../../components/user/CommissionCards.vue";
import Score from "../../../components/user/Score.vue";
import SellerFarol from "../../../components/user/metrics/SellerFarol.vue";
import SellerSnapshotPanel from "../../../components/user/metrics/SellerSnapshotPanel.vue";
import SellerPortraitGrid from "../../../components/user/metrics/SellerPortraitGrid.vue";
import SellerAiAudit from "../../../components/user/metrics/SellerAiAudit.vue";
import RiskCaseBanner from "../../../components/user/metrics/RiskCaseBanner.vue";
import RiskDecisionBar from "../../../components/user/metrics/RiskDecisionBar.vue";
import MetricsTour from "../../../components/user/metrics/MetricsTour.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import CommissionsChart from "../../users/commissions/CommissionsChart.vue";
import UserBinUsageCard from "../../../components/user/UserBinUsageCard.vue";
import {
    ExclamationCircleIcon,
    CreditCardIcon,
    BanknotesIcon,
    ChartBarIcon,
} from "@heroicons/vue/24/outline";
import {
    FileTextIcon,
    HistoryIcon,
    LoaderCircleIcon,
    SparklesIcon,
} from "@lucide/vue";
import ReserveRunoffCard from "../../../components/user/metrics/ReserveRunoffCard.vue";
import UserSystemAnalysis from "../../../components/user/UserSystemAnalysis.vue";
import UserPaymentMethodCard from "../../../components/user/UserPaymentMethodCard.vue";
import UserCommissionTypesCard from "../../../components/user/UserCommissionTypesCard.vue";
import CollapsibleInfoCard from "../../../components/user/CollapsibleInfoCard.vue";

const STATUS_RANK = { critical: 2, attention: 1 };

const TRIGGER_LABELS = {
    parcela_grande: "Parcela grande",
    extensao_manual: "Extensão manual",
    churn_detectado: "Churn",
    cauda_final: "Cauda final",
    venda_sem_retencao: "Venda sem retenção",
    manual: "Manual",
};

const DECISION_LABELS = {
    liberar: "Liberação aprovada",
    estender: "Reserva estendida",
    travar_saque: "Saque travado",
};

/* Alvos de navegação que vivem dentro de outra visão. */
const TARGET_VIEW = { score: "conduta" };

const PRIORITY_DOTS = {
    danger: "bg-rose-500",
    warning: "bg-amber-500",
};

export default {
    components: {
        CommissionsChart,
        SellerFarol,
        SellerSnapshotPanel,
        SellerPortraitGrid,
        SellerAiAudit,
        RiskCaseBanner,
        RiskDecisionBar,
        MetricsTour,
        Score,
        CommissionCards,
        UserBinUsageCard,
        ExclamationCircleIcon,
        CreditCardIcon,
        BanknotesIcon,
        ChartBarIcon,
        FileTextIcon,
        HistoryIcon,
        LoaderCircleIcon,
        SparklesIcon,
        ReserveRunoffCard,
        UserSystemAnalysis,
        UserPaymentMethodCard,
        UserCommissionTypesCard,
        CollapsibleInfoCard,
    },
    props: ["user"],
    data() {
        return {
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            // Janela de análise aplicada a todas as métricas temporais da tela
            period: 30,
            // 0 = Total (sem janela, todo o histórico)
            periodOptions: [7, 15, 30, 90, 0],
            // Visão aberta no painel e visões já abertas (carga sob demanda)
            view: "overview",
            visited: { overview: true },
            // Altura do workspace: ocupa a viewport abaixo do cabeçalho
            workspaceHeight: null,
            // Retrato: buscado uma vez aqui e distribuído para os julgamentos
            // e as fichas.
            portrait: null,
            portraitLoading: true,
            portraitError: false,
            portraitSeq: 0,
            // Caso de risco: checkpoints de reserva do seller (pendente =
            // caso aberto → banner + barra de decisão)
            checkpoints: [],
            checkpointsSeq: 0,
            extendOptions: [15, 30, 45, 60],
            decidedRecently: false,
            runoffKey: 0,
            // Destaque temporário disparado por julgamentos, agente e tour
            flash: null,
            flashTimer: null,
            // Dados para os summaries dos cards
            userAnalysis: null,
            scoreSummary: "",
            scorePriority: "normal",
            scoreBadge: null,
            scoreAmount: null,
            binSummary: "",
            binPriority: "normal",
            binBadge: null,
            paymentMethodSummary: "",
            paymentMethodPriority: "normal",
            paymentMethodBadge: null,
            systemAnalysisSummary: "",
            systemAnalysisPriority: "normal",
            systemAnalysisBadge: null,
            commissionTypesSummary: "",
            commissionTypesPriority: "normal",
            commissionTypesBadge: null,
            runoffSummary: "",
            runoffPriority: "normal",
            runoffBadge: null,
            runoffHeadline: "",
            // Estado do botão de gerar dossiê
            generatingDossier: false,
        };
    },

    mixins: [HelperMixin],

    computed: {
        chartFilters() {
            return {
                ...this.filters,
                interval: this.period,
            };
        },

        periodLabel() {
            return this.period === 0 ? "Total" : `Últimos ${this.period} dias`;
        },

        periodRangeLabel() {
            if (this.period === 0) {
                return "todo o histórico";
            }

            const start = new Date();
            start.setDate(start.getDate() - this.period);

            const format = (date) =>
                date.toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                });

            return `${format(start)} – ${format(new Date())}`;
        },

        workspaceStyle() {
            return this.workspaceHeight
                ? { height: `${this.workspaceHeight}px` }
                : {};
        },

        /**
         * Visões do painel, na ordem das abas. A prioridade acende o ponto
         * da aba e o mesmo dado alimenta a paleta de comandos do agente.
         */
        viewTabs() {
            return [
                {
                    key: "overview",
                    label: "Visão geral",
                    description: "Julgamentos do caso e fichas do retrato.",
                    summary: this.overviewSummary,
                    priority: this.overviewPriority,
                    question: "Gerar parecer completo",
                },
                {
                    key: "runoff",
                    label: "Reserva & Runoff",
                    description: "Agenda da reserva e risco que ainda entra.",
                    summary: this.runoffSummary,
                    priority: this.runoffPriority,
                    question: "Posso liberar o saque?",
                },
                {
                    key: "conduta",
                    label: "Conduta & Score",
                    description: "Disputas, reembolsos, score e BINs.",
                    summary: this.condutaSummary,
                    priority: this.condutaPriority,
                    question: "Tem sinal de fraude?",
                },
                {
                    key: "operacao",
                    label: "Operação & Vendas",
                    description: `Vendas, comissões e métodos · ${this.periodLabel.toLowerCase()}.`,
                    summary: this.operacaoSummary,
                    priority: this.operacaoPriority,
                    question: "O negócio está vivo?",
                },
                {
                    key: "hist",
                    label: "Histórico",
                    description: "Decisões de reserva registradas.",
                    summary: this.historySummary,
                    priority: "normal",
                    question: "Resuma o histórico de decisões",
                },
                {
                    key: "retrato",
                    label: "Retrato",
                    description:
                        "Fichas, réguas e evidências do retrato completo.",
                    summary: "",
                    priority: "normal",
                    question: "Explique o retrato do seller",
                },
            ];
        },

        overviewPriority() {
            const rank = Math.max(
                0,
                ...this.farolSignals.map(
                    (signal) => STATUS_RANK[signal.status] ?? 0,
                ),
            );

            return rank === 2 ? "danger" : rank === 1 ? "warning" : "normal";
        },

        overviewSummary() {
            if (this.portraitLoading) {
                return "";
            }

            const pending = this.farolSignals.filter(
                (signal) => signal.status !== "ok",
            ).length;

            if (this.openCheckpoint) {
                return `Caso aberto · ${this.caseMeta}`;
            }

            if (pending === 0) {
                return "Os três julgamentos estão dentro do limiar.";
            }

            return `${pending} ${pending === 1 ? "julgamento pede" : "julgamentos pedem"} atenção.`;
        },

        /** Checkpoint pendente mais urgente: é o "caso aberto" da tela. */
        openCheckpoint() {
            return (
                this.checkpoints.find(
                    (checkpoint) => checkpoint.status === "pending",
                ) ?? null
            );
        },

        decidedCheckpoints() {
            return this.checkpoints.filter((checkpoint) =>
                ["decided", "auto_resolved", "expired"].includes(
                    checkpoint.status,
                ),
            );
        },

        caseHeadline() {
            return (
                this.runoffHeadline ||
                this.openCheckpoint?.recommendation?.reason ||
                "Há um checkpoint de reserva aguardando decisão."
            );
        },

        caseMeta() {
            const checkpoint = this.openCheckpoint;

            if (!checkpoint) {
                return "";
            }

            const parts = [
                TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger,
            ];

            if (checkpoint.due_date) {
                parts.push(
                    `decidir até ${this.shortDate(checkpoint.due_date)}`,
                );
            }

            return parts.join(" · ");
        },

        historySummary() {
            const count = this.decidedCheckpoints.length;

            if (count === 0) {
                return "";
            }

            return `${count} ${count === 1 ? "decisão registrada" : "decisões registradas"}.`;
        },

        condutaPriority() {
            const worst = this.worstCard([
                "disputes",
                "box_disputes",
                "refunds",
            ]);
            const rank = STATUS_RANK[worst?.status] ?? 0;
            const scoreRank =
                { danger: 2, warning: 1 }[this.scorePriority] ?? 0;

            const combined = Math.max(rank, scoreRank);

            return combined === 2
                ? "danger"
                : combined === 1
                  ? "warning"
                  : "normal";
        },

        condutaSummary() {
            const parts = [];
            const disputes = this.cardByKey("disputes");

            if (disputes) {
                parts.push(`Disputas ${disputes.value}`);
            }

            if (this.scoreAmount !== null) {
                parts.push(`score ${this.scoreAmount}`);
            }

            return parts.join(" · ");
        },

        operacaoPriority() {
            const worst = this.worstCard(["days_with_sale", "sales_count"]);

            return worst?.status === "critical"
                ? "danger"
                : worst?.status === "attention"
                  ? "warning"
                  : "normal";
        },

        operacaoSummary() {
            const tpv = this.cardByKey("tpv");
            const days = this.cardByKey("days_with_sale");
            const parts = [];

            if (tpv) {
                parts.push(`Vendas ${tpv.value}`);
            }

            if (days) {
                parts.push(`${days.value} dias com venda`);
            }

            return parts.join(" · ");
        },

        /**
         * Os três julgamentos, derivados dos status que já estão na tela
         * (cards do retrato + veredito do runoff). Sem request extra e sem
         * divergir do que as visões mostram.
         */
        farolSignals() {
            const caixa = this.worstCard(["coverage", "reserve", "cashouts"]);
            const operacao = this.worstCard(["days_with_sale"]);
            const conduta = this.worstCard([
                "disputes",
                "box_disputes",
                "refunds",
            ]);

            const signals = [
                {
                    key: "conduta",
                    domain: "Conduta",
                    question: "Tem sinal de fraude?",
                    section: "conduta",
                    cta: "ver conduta & score →",
                    ...this.signalOf(
                        conduta,
                        "disputas e reembolsos dentro do limiar",
                    ),
                },
                {
                    key: "caixa",
                    domain: "Caixa",
                    question: "Posso liberar dinheiro?",
                    section: "runoff",
                    cta: "ver reserva & runoff →",
                    ...this.signalOf(
                        caixa,
                        "cobertura e saques dentro do limiar",
                    ),
                },
                {
                    key: "operacao",
                    domain: "Operação",
                    question: "O negócio está vivo?",
                    section: "operacao",
                    cta: "ver operação & vendas →",
                    ...this.signalOf(operacao, "vendendo em ritmo normal"),
                },
            ];

            // O runoff pode ser pior que os cards de caixa (parcela descoberta)
            const runoffRank =
                { danger: 2, warning: 1 }[this.runoffPriority] ?? 0;

            const caixaSignal = signals.find(
                (signal) => signal.key === "caixa",
            );

            if (runoffRank > (STATUS_RANK[caixa?.status] ?? 0)) {
                caixaSignal.status =
                    runoffRank === 2 ? "critical" : "attention";
                caixaSignal.reason = this.runoffSummary || caixaSignal.reason;
            }

            return signals;
        },
    },

    watch: {
        period() {
            // Evita misturar valores de janelas diferentes: some o resumo até o novo chegar
            this.binSummary = "";
            this.paymentMethodSummary = "";
            this.fetchPortrait();
        },

        user() {
            // Troca de seller: zera o caso na hora. A barra de decisão não
            // pode ficar nem um frame com o checkpoint do seller anterior.
            this.checkpoints = [];
            this.decidedRecently = false;
            this.view = "overview";
            this.fetchPortrait();
            this.fetchCheckpoints();
        },
    },

    mounted() {
        this.fetchPortrait();
        this.fetchCheckpoints();

        this.$nextTick(() => this.fitWorkspace());
        window.addEventListener("resize", this.fitWorkspace);
    },

    beforeUnmount() {
        clearTimeout(this.flashTimer);
        window.removeEventListener("resize", this.fitWorkspace);
    },

    methods: {
        /**
         * Workspace com a altura da viewport abaixo do cabeçalho do user:
         * conversa e painel rolam por dentro, como num app. Em telas
         * estreitas (empilhado) a altura é natural.
         */
        fitWorkspace() {
            const el = this.$refs.workspace;

            if (!el || window.innerWidth < 1024) {
                this.workspaceHeight = null;
                return;
            }

            const top = el.getBoundingClientRect().top + window.scrollY;

            this.workspaceHeight = Math.max(600, window.innerHeight - top - 16);
        },

        fetchPortrait() {
            const seq = ++this.portraitSeq;

            this.portraitLoading = true;
            this.portraitError = false;

            axios
                .get(`/api/user/${this.userId()}/metrics/portrait`, {
                    params: { period: this.period },
                })
                .then(({ data }) => {
                    // O operador pode trocar de período antes da resposta
                    // anterior chegar: só publica a última pedida.
                    if (seq === this.portraitSeq) {
                        this.portrait = data;
                    }
                })
                .catch(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitError = true;
                    }
                })
                .finally(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitLoading = false;
                    }
                });
        },

        fetchCheckpoints() {
            // Mesma guarda de sequência do retrato: a barra de decisão posta
            // no checkpoint carregado aqui, e uma resposta atrasada do seller
            // anterior registraria a decisão no seller errado.
            const seq = ++this.checkpointsSeq;

            axios
                .get(
                    `/api/financial/users/${this.userId()}/reserve-checkpoints`,
                )
                .then(({ data }) => {
                    if (seq !== this.checkpointsSeq) {
                        return;
                    }

                    this.checkpoints = data.checkpoints ?? [];
                    this.extendOptions =
                        data.extend_days_options ?? this.extendOptions;
                })
                .catch(() => {
                    if (seq === this.checkpointsSeq) {
                        this.checkpoints = [];
                    }
                });
        },

        onDecided() {
            this.decidedRecently = true;
            this.fetchCheckpoints();
            // Remonta o card de runoff: estender muda a agenda e as demais
            // decisões mudam a lista de checkpoints exibida lá dentro.
            this.runoffKey += 1;
        },

        startTour() {
            this.$refs.tour?.start();
        },

        onTourHighlight(key) {
            if (key === null) {
                this.clearFlash();
                return;
            }

            if (key === "julg") {
                this.openView("overview");
            } else if (key === "runoff") {
                this.openView("runoff");
            }

            this.applyFlash(key, false);
        },

        /** Abre uma visão no painel e volta o scroll para o topo. */
        openView(key) {
            this.view = key;
            this.visited[key] = true;

            this.$nextTick(() => {
                this.$refs.canvas?.scrollTo({ top: 0 });
                // Gráficos montados escondidos recalculam a largura
                window.dispatchEvent(new Event("resize"));
            });
        },

        /**
         * Navegação "onde está X": abre a visão alvo no painel e aplica o
         * flash de destaque. Usada pelos julgamentos, pelo agente e pelos
         * cartões de evidência das respostas.
         */
        goNav(target) {
            const view = TARGET_VIEW[target] ?? target;

            if (!this.viewTabs.some((tab) => tab.key === view)) {
                return;
            }

            this.openView(view);
            this.applyFlash(target);

            if (target === "score") {
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.$refs.score?.[0]?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });
                    }, 80);
                });
            }
        },

        askAgent(question) {
            this.$refs.agentPane?.sendMessage(question);
        },

        focusDecision() {
            this.applyFlash("bar");
        },

        applyFlash(key, autoClear = true) {
            clearTimeout(this.flashTimer);
            this.flash = key;

            if (autoClear) {
                this.flashTimer = setTimeout(() => {
                    this.flash = null;
                }, 2800);
            }
        },

        clearFlash() {
            clearTimeout(this.flashTimer);
            this.flash = null;
        },

        flashClass(key, inset = false) {
            if (this.flash !== key) {
                return "";
            }

            return inset
                ? "ring-3 ring-inset ring-indigo-500/50"
                : "ring-3 ring-indigo-500/50 shadow-[0_8px_28px_rgba(91,75,232,0.22)]";
        },

        priorityDot(priority) {
            return PRIORITY_DOTS[priority] ?? null;
        },

        userId() {
            return this.$route?.params?.user_id ?? this.user.id;
        },

        cardByKey(key) {
            return (this.portrait?.cards || []).find(
                (card) => card.key === key,
            );
        },

        worstCard(keys) {
            const byKey = new Map(
                (this.portrait?.cards || []).map((card) => [card.key, card]),
            );

            let worst = null;

            for (const key of keys) {
                const card = byKey.get(key);

                if (
                    card &&
                    (!worst ||
                        (STATUS_RANK[card.status] ?? 0) >
                            (STATUS_RANK[worst.status] ?? 0))
                ) {
                    worst = card;
                }
            }

            return worst;
        },

        signalOf(card, okReason) {
            if (card && (STATUS_RANK[card.status] ?? 0) > 0) {
                return {
                    status: card.status,
                    reason: `${card.label}: ${card.value}`,
                };
            }

            return { status: "ok", reason: okReason };
        },

        shortDate(value) {
            if (!value) {
                return "";
            }

            const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);

            return isNaN(parsed)
                ? value
                : parsed.toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                  });
        },

        historyTitle(checkpoint) {
            if (checkpoint.status === "decided") {
                return `${DECISION_LABELS[checkpoint.decision] ?? checkpoint.decision} — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
            }

            if (checkpoint.status === "auto_resolved") {
                return `Critério passou — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
            }

            return `Checkpoint expirado — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
        },

        historyMeta(checkpoint) {
            const parts = [];

            if (checkpoint.decided_at) {
                parts.push(
                    new Date(checkpoint.decided_at).toLocaleDateString(
                        "pt-BR",
                        { day: "2-digit", month: "2-digit", year: "numeric" },
                    ),
                );
            } else if (checkpoint.due_date) {
                parts.push(`prazo ${this.shortDate(checkpoint.due_date)}`);
            }

            return parts.join(" · ");
        },

        updateScoreSummary(scoreData) {
            if (!scoreData) {
                this.scoreSummary = "Score não disponível";
                this.scorePriority = "normal";
                this.scoreBadge = null;
                this.scoreAmount = null;
                return;
            }

            const { amount, level, bypass, historical } = scoreData;

            this.scoreAmount = amount;

            // Determina a prioridade baseada no score (escala 15-100, bloqueio em 50)
            if (amount < 50) {
                this.scorePriority = "danger";
            } else if (amount < 75) {
                this.scorePriority = "warning";
            } else {
                this.scorePriority = "normal";
            }

            // Badge com informações especiais
            let badgeText = level.charAt(0).toUpperCase() + level.slice(1);
            if (bypass) {
                badgeText = "Bypass ativo";
                this.scorePriority = "warning";
            } else if (historical) {
                badgeText = "Histórico";
            }

            this.scoreBadge = badgeText;
            this.scoreSummary = `Score ${amount} (${level})${bypass ? " - Bypass ativo" : ""}${historical ? " - Visualizando histórico" : ""}`;
        },

        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = "Nenhum BIN encontrado";
                this.binPriority = "normal";
                this.binBadge = null;
                return;
            }

            const topBins = data.top_bins;
            const summary = data.summary;

            // Determina prioridade baseada no resumo geral se disponível
            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = "danger";
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = "warning";
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                // Fallback para análise dos top bins se não houver resumo
                const highFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "high",
                ).length;
                const mediumFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "medium",
                ).length;
                const totalBins = summary?.total_unique_bins || topBins.length;

                if (highFraudBins > 0) {
                    this.binPriority = "danger";
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = "warning";
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = "normal";
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },

        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = "Nenhum método encontrado";
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (method) => method.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (method) => method.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Alta concentração";
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Concentração média";
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },

        updateSystemAnalysisSummary(analysis) {
            this.userAnalysis = analysis;

            if (!analysis) {
                this.systemAnalysisSummary = "Análise não disponível";
                this.systemAnalysisPriority = "normal";
                this.systemAnalysisBadge = null;
                return;
            }

            // Determina a prioridade baseada na confiança e análise
            if (analysis.analysis === "Reprovar" || analysis.confidence < 30) {
                this.systemAnalysisPriority = "danger";
            } else if (
                analysis.analysis === "Atenção" ||
                (analysis.confidence >= 30 && analysis.confidence < 70)
            ) {
                this.systemAnalysisPriority = "warning";
            } else {
                this.systemAnalysisPriority = "normal";
            }

            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },

        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary =
                    "Nenhum tipo de comissão encontrado";
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (commission) => commission.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (commission) => commission.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Alta concentração";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Concentração média";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },

        updateRunoffSummary(runoff) {
            // O card já emite o resumo pronto (ui_summary): a leitura de
            // status e a formatação vivem no próprio card, não aqui.
            if (!runoff?.ui_summary) {
                this.runoffSummary = "Runoff não disponível";
                this.runoffPriority = "normal";
                this.runoffBadge = null;
                this.runoffHeadline = "";
                return;
            }

            this.runoffPriority = runoff.ui_summary.priority;
            this.runoffBadge = runoff.ui_summary.badge;
            this.runoffSummary = runoff.ui_summary.summary;
            this.runoffHeadline = runoff.ui_summary.headline ?? "";
        },

        generateDossier() {
            this.generatingDossier = true;

            const userId = this.$route.params.user_id ?? this.user.id;

            axios
                .post(`/api/user/${userId}/dossier/generate`)
                .then((response) => {
                    this.generatingDossier = false;

                    if (response.data.success && response.data.download_url) {
                        // Abrir URL em nova aba
                        window.open(response.data.download_url, "_blank");
                    } else {
                        throw new Error("Resposta inválida do servidor");
                    }
                })
                .catch(() => {
                    this.generatingDossier = false;
                });
        },
    },
};
</script>
